<script>
import { mapGetters } from 'vuex';
import { getSidebarItems } from './config/default-sidebar';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useRoute, useRouter } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PrimarySidebar from './sidebarComponents/Primary.vue';
import SecondarySidebar from './sidebarComponents/Secondary.vue';

export default {
  components: {
    PrimarySidebar,
    SecondarySidebar,
  },
  props: {
    showSecondarySidebar: { type: Boolean, default: true },
    sidebarClassName: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const { shouldShow } = usePolicy();

    const toggleKeyShortcutModal = () => {
      emit('openKeyShortcutModal');
    };
    const closeKeyShortcutModal = () => {
      emit('closeKeyShortcutModal');
    };
    const navigateToRoute = routeName => {
      if (route.name !== routeName) {
        router.push({ name: routeName });
      }
    };
    const keyboardEvents = {
      '$mod+Slash': { action: toggleKeyShortcutModal },
      '$mod+Escape': { action: closeKeyShortcutModal },
      'Alt+KeyC': { action: () => navigateToRoute('home') },
      'Alt+KeyV': { action: () => navigateToRoute('contacts_dashboard') },
      'Alt+KeyR': { action: () => navigateToRoute('account_overview_reports') },
      'Alt+KeyS': { action: () => navigateToRoute('agent_list') },
    };
    useKeyboardEvents(keyboardEvents);
    return { shouldShow };
  },
  data() {
    return {
      showOptionsMenu: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      inboxes: 'inboxes/getInboxes',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
      labels: 'labels/getLabelsOnSidebar',
      internalChatTotalUnread: 'internalChat/totalUnreadCount',
    }),
    teams() {
      return this.currentUser.role === 'administrator'
        ? this.$store.getters['teams/getTeams']
        : this.$store.getters['teams/getMyTeams'];
    },
    activeCustomView() {
      if (this.activePrimaryMenu.key === 'contacts') return 'contact';
      if (this.activePrimaryMenu.key === 'conversations') return 'conversation';
      return '';
    },
    customViews() {
      if (!this.activeCustomView) {
        return [];
      }
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        this.activeCustomView
      ) || [];
    },
    isConversationOrContactActive() {
      return (
        this.activePrimaryMenu.key === 'contacts' ||
        this.activePrimaryMenu.key === 'conversations'
      );
    },
    sideMenuConfig() {
      return getSidebarItems(this.accountId);
    },
    primaryMenuItems() {
      const menuItems = this.sideMenuConfig.primaryMenu;
      return menuItems.filter(menuItem => {
        const permissions = menuItem.meta?.permissions || [];
        const installationTypes = menuItem.meta?.installationTypes || [];
        if (!this.shouldShow(menuItem.featureFlag, permissions, installationTypes)) {
          return false;
        }
        if (menuItem.featureFlag) {
          return this.isFeatureEnabledonAccount(
            this.accountId,
            menuItem.featureFlag
          );
        }
        return true;
      });
    },
    activeSecondaryMenu() {
      const { secondaryMenu } = this.sideMenuConfig;
      const { name: currentRoute } = this.$route;
      return (
        secondaryMenu.find(menuItem =>
          menuItem.routes.includes(currentRoute)
        ) || {}
      );
    },
    activePrimaryMenu() {
      const { name: currentRoute } = this.$route;
      const directPrimaryMatch =
        this.primaryMenuItems.find(menuItem => {
          const activeRoutes = [
            menuItem.toStateName,
            ...(menuItem.activeOn || []),
          ].filter(Boolean);
          return activeRoutes.includes(currentRoute);
        }) || {};

      if (directPrimaryMatch.key) {
        return directPrimaryMatch;
      }

      return (
        this.primaryMenuItems.find(
          menuItem => menuItem.key === this.activeSecondaryMenu.parentNav
        ) || {}
      );
    },
  },
  watch: {
    activeCustomView() {
      this.fetchCustomViews();
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('notifications/unReadCount');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('attributes/get');
    this.fetchCustomViews();
    if (
      this.accountId &&
      this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.INTERNAL_CHAT
      )
    ) {
      this.$store.dispatch('internalChat/fetchRooms');
    }
  },
  methods: {
    fetchCustomViews() {
      if (this.isConversationOrContactActive) {
        this.$store.dispatch('customViews/get', this.activeCustomView);
      }
    },
    toggleAccountModal() {
      this.$emit('toggleAccountModal');
    },
    showAddLabelPopup() {
      this.$emit('showAddLabelPopup');
    },
    showDashboardAppPopup() {
      this.$emit('dashboardAppModal');
    },
    openNotificationPanel() {
      this.$emit('openNotificationPanel');
    },
  },
};
</script>

<template>
  <aside class="flex h-full">
    <PrimarySidebar
      :logo-thumbnail="globalConfig.logoThumbnail"
      :logo-thumbnail-dark="globalConfig.logoThumbnailDark"
      :installation-name="globalConfig.installationName"
      :account-id="accountId"
      :menu-items="primaryMenuItems"
      :active-menu-item="activePrimaryMenu.key"
      :internal-chat-unread-count="internalChatTotalUnread"
      @toggle-accounts="toggleAccountModal"
      @open-notification-panel="openNotificationPanel"
    />
    <SecondarySidebar
      v-if="showSecondarySidebar"
      :class="sidebarClassName"
      :account-id="accountId"
      :inboxes="inboxes"
      :labels="labels"
      :teams="teams"
      :custom-views="customViews"
      :menu-config="activeSecondaryMenu"
      :is-on-chatwoot-cloud="isOnChatwootCloud"
      @add-label="showAddLabelPopup"
      @dashboard-app-modal="showDashboardAppPopup"
      @toggle-accounts="toggleAccountModal"
    />
  </aside>
</template>
